import { Tooltip, Typography } from '@mui/material';
import { Stack, TooltipProps } from '@mui/material';
import { Assessment } from '@schooly/api';
import { NewTabIcon } from '@schooly/style';
import { SimpleButton } from '@schooly/style';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

type AssessmentReportsTooltipProps<T> = {
  assessment: T;
} & Omit<TooltipProps, 'title'>;

export const AssessmentReportsTooltip = <
  T extends Pick<Assessment, 'autogenerated' | 'reports' | 'generated_from_report_id'>,
>({
  assessment,
  children,
  ...props
}: AssessmentReportsTooltipProps<T>) => {
  const { formatMessage } = useIntl();

  const renderTitle = () => {
    if (assessment.autogenerated) {
      return (
        <Stack alignItems="flex-start" gap={1}>
          <Typography>
            {formatMessage(
              { id: 'assessments-AutoGenerated' },
              { reportName: assessment.reports[0]?.name },
            )}
          </Typography>
          {assessment.generated_from_report_id && (
            <Link to={`/reports/${assessment.generated_from_report_id}`} target="_blank">
              <SimpleButton
                sx={(theme) => ({
                  marginTop: theme.spacing(1),
                  '& .MuiButton-startIcon .svg-icon': {
                    fontSize: theme.spacing(2),
                  },
                })}
                startIcon={<NewTabIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Typography>{formatMessage({ id: 'assessments-GoToReport' })}</Typography>
              </SimpleButton>
            </Link>
          )}
        </Stack>
      );
    }

    if (!assessment.reports.length) return;

    return (
      <Stack gap={2}>
        {assessment.reports.map((r) => (
          <Typography
            component={Link}
            to={`/reports/${r.id}`}
            target="_blank"
            key={r.id}
            sx={{ '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}
          >
            {r.name}
          </Typography>
        ))}
      </Stack>
    );
  };

  return (
    <Tooltip
      componentsProps={{
        tooltip: { sx: { p: 1.25, minWidth: 200, maxHeight: 400, overflowY: 'scroll' } },
      }}
      title={renderTitle()}
      {...props}
    >
      {children}
    </Tooltip>
  );
};
